import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Share2,
  UserPlus,
  Trash2,
  Eye,
  Edit,
  Loader2,
  X,
  Mail,
  Send,
  Link2,
  Copy,
  Check,
  Clock
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

const PatientSharing = ({ patientId }) => {
  const [shares, setShares] = useState([]);
  const [shareLinks, setShareLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState('view');
  const [submitting, setSubmitting] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  const fetchShares = async () => {
    try {
      const [sharesRes, linksRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cases/patients/${patientId}/shares`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/cases/patients/${patientId}/share-links`, { withCredentials: true })
      ]);
      setShares(sharesRes.data.shares || []);
      setShareLinks(linksRes.data.links || []);
    } catch (error) {
      logger.error('Error fetching shares:', error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchShares(); }, [patientId]);

  const handleShare = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/cases/patients/${patientId}/share`, {
        email, permission
      }, { withCredentials: true });
      toast.success(data.message);
      setEmail('');
      setShowForm(false);
      fetchShares();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnshare = async (share) => {
    const identifier = share.share_type === 'external'
      ? share.shared_with_email
      : share.shared_with_user_id;
    try {
      await axios.delete(`${BACKEND_URL}/api/cases/patients/${patientId}/share/${encodeURIComponent(identifier)}`, { withCredentials: true });
      toast.success('Access removed');
      fetchShares();
    } catch (error) {
      toast.error('Failed to remove access');
    }
  };

  const handleGenerateLink = async () => {
    setGeneratingLink(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/cases/patients/${patientId}/share-link`, {}, { withCredentials: true });
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success('Share link created & copied to clipboard!');
      } catch (clipboardError) {
        // Clipboard may fail in some contexts, but link was still created
        toast.success('Share link created!');
      }
      fetchShares();
    } catch (error) {
      toast.error('Failed to generate share link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLink = async (token) => {
    const url = `${window.location.origin}/shared/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      toast.success('Link copied!');
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleRevokeLink = async (token) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/cases/patients/${patientId}/share-link/${token}`, { withCredentials: true });
      toast.success('Share link revoked');
      fetchShares();
    } catch (error) {
      toast.error('Failed to revoke link');
    }
  };

  const formatExpiry = (isoStr) => {
    const d = new Date(isoStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-4" data-testid="patient-sharing">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-[#5C5F5C] uppercase tracking-wider flex items-center gap-1">
          <Share2 className="w-4 h-4" />
          Shared With ({shares.length})
        </h4>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleGenerateLink}
            disabled={generatingLink}
            className="text-[#4B6651] gap-1 text-xs"
            data-testid="generate-share-link-btn"
          >
            {generatingLink ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">Link</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowForm(!showForm)}
            className="text-[#4B6651]"
            data-testid="share-patient-btn"
          >
            {showForm ? <X className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Email share form */}
      {showForm && (
        <form onSubmit={handleShare} className="space-y-2 p-3 bg-[#F8F7F4] rounded-lg" data-testid="share-form">
          <p className="text-xs text-[#5C5F5C]">Share with any email address. If they don't have an account, they'll receive an invite.</p>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 border-[#E5E4E0] bg-white h-9 text-sm"
              data-testid="share-email-input"
            />
            <Select value={permission} onValueChange={setPermission}>
              <SelectTrigger className="w-24 h-9 text-xs border-[#E5E4E0] bg-white" data-testid="share-permission-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="view">View</SelectItem>
                <SelectItem value="edit">Edit</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" size="sm" disabled={submitting} className="bg-[#4B6651] hover:bg-[#3E5543] text-white h-9" data-testid="submit-share-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </form>
      )}

      {/* Active share links */}
      {shareLinks.length > 0 && (
        <div className="space-y-1.5" data-testid="share-links-section">
          <p className="text-xs font-medium text-[#5C5F5C] uppercase tracking-wider flex items-center gap-1">
            <Link2 className="w-3 h-3" /> Active Links ({shareLinks.length})
          </p>
          {shareLinks.map((link) => (
            <div key={link.token} className="flex items-center justify-between p-2 bg-[#F0F4F1] rounded-lg" data-testid={`share-link-${link.token}`}>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-full bg-[#4B6651]/10 flex items-center justify-center flex-shrink-0">
                  <Link2 className="w-3.5 h-3.5 text-[#4B6651]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#2A2C2A] truncate">
                    View-only link
                  </p>
                  <p className="text-xs text-[#5C5F5C] flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Expires {formatExpiry(link.expires_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopyLink(link.token)}
                  className="text-[#4B6651] hover:text-[#3E5543]"
                  data-testid={`copy-link-${link.token}`}
                >
                  {copiedToken === link.token ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRevokeLink(link.token)}
                  className="text-[#5C5F5C] hover:text-[#D94F4F]"
                  data-testid={`revoke-link-${link.token}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Existing shares list */}
      {loading ? (
        <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#4B6651] mx-auto" /></div>
      ) : shares.length > 0 ? (
        <div className="space-y-2">
          {shares.map((share, idx) => (
            <div key={share.shared_with_user_id || share.shared_with_email || idx} className="flex items-center justify-between p-2 bg-[#F8F7F4] rounded-lg" data-testid={`share-${share.shared_with_user_id || share.shared_with_email}`}>
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                  share.share_type === 'external' ? 'bg-[#C06144]/10' : 'bg-[#4B6651]/10'
                }`}>
                  {share.share_type === 'external' ? (
                    <Mail className="w-3.5 h-3.5 text-[#C06144]" />
                  ) : share.share_type === 'link' ? (
                    <Link2 className="w-3.5 h-3.5 text-[#4B6651]" />
                  ) : (
                    <span className="text-xs font-medium text-[#4B6651]">
                      {share.shared_with_name?.charAt(0) || share.shared_with_email?.charAt(0) || '?'}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#2A2C2A]">
                    {share.shared_with_name || share.shared_with_email}
                    {share.share_type === 'external' && (
                      <span className="ml-1.5 text-xs px-1.5 py-0.5 bg-[#C06144]/10 text-[#C06144] rounded">Invited</span>
                    )}
                    {share.share_type === 'link' && (
                      <span className="ml-1.5 text-xs px-1.5 py-0.5 bg-[#4B6651]/10 text-[#4B6651] rounded">Via link</span>
                    )}
                  </p>
                  <p className="text-xs text-[#5C5F5C] flex items-center gap-1">
                    {share.permission === 'edit' ? <Edit className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    {share.permission}
                    {share.shared_with_email && share.share_type === 'external' && (
                      <span className="ml-1">({share.shared_with_email})</span>
                    )}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleUnshare(share)}
                className="text-[#5C5F5C] hover:text-[#D94F4F]"
                data-testid={`unshare-${share.shared_with_user_id || share.shared_with_email}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        shareLinks.length === 0 && <p className="text-sm text-[#5C5F5C] text-center py-2">Not shared with anyone</p>
      )}
    </div>
  );
};

export default PatientSharing;
